import _ from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {mapToSignatureIds, selectSortedSignaturesInNamespace} from '../../../helpers/models/signatureHelpers';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../helpers/reselectHelpers';
import {SignatureModel} from '../../../models/api/signatureModel';
import {SettingsSignaturesUrl} from '../../../models/url/settingsUrlModel';
import {AppState} from '../../../store/appState';
import {markSignaturesAsStaleAction} from '../../../store/data/signatures/signaturesActions';
import {
  getFlatSignatures,
  getSignaturesAsyncStatus,
} from '../../../store/data/signatures/signaturesSelectors';
import {fetchSignaturesForSettingsTableAsyncAction} from '../../../store/panels/settings/settingsPanelActions';
import {getSettingsPanelResources} from '../../../store/panels/settings/settingsPanelSelectors';
import {isSettingsResourcesSignaturesResource} from '../../../store/panels/settings/settingsPanelState';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';
import {SignaturesTable} from './signaturesTable';

/*
 * Props.
 */

interface SignaturesTableContainerProps {
  signaturesUrl: SettingsSignaturesUrl;
  searchQuery?: string;
}

/*
 * Selectors.
 */

const isSearchQueryMatch = (searchQuery: string, signature?: SignatureModel): boolean => {
  if (!signature) {
    return false;
  }
  const searchQueryLower = searchQuery.toLowerCase();
  const matchesBlurb = signature.blurb.toLowerCase().includes(searchQueryLower);
  const matchesName = signature.name.toLowerCase().includes(searchQueryLower);

  return matchesBlurb || matchesName;
};

const filterBySearchQuery = (
  signatures: ReadonlyArray<SignatureModel>,
  searchQuery?: string,
): ReadonlyArray<number> =>
  searchQuery
    ? _(signatures)
        .filter((signature) => isSearchQueryMatch(searchQuery, signature))
        .map((signature) => signature.id)
        .compact()
        .value()
    : mapToSignatureIds(signatures);

function getCurrentNamespace(_state: AppState, props: SignaturesTableContainerProps) {
  return props.signaturesUrl.namespace;
}

function makeGetSignatureIds() {
  return createSelector(
    [getSettingsPanelResources, getCurrentNamespace, getSearchQuery, getFlatSignatures],
    (settingsResources, namespace, searchQuery, signatures) => {
      if (!settingsResources || !isSettingsResourcesSignaturesResource(settingsResources)) {
        return [];
      }
      const signaturesInCurrentNamespace = selectSortedSignaturesInNamespace(signatures, namespace);
      return filterBySearchQuery(signaturesInCurrentNamespace, searchQuery);
    },
  );
}

function getSearchQuery(_state: AppState, props: SignaturesTableContainerProps) {
  return props.searchQuery;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<SignaturesTableContainerProps>()(() => ({
  signatureIds: makeGetSignatureIds(),
  loadingStatus: getSignaturesAsyncStatus,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  navigateToLink: navigateToLinkAction,
  fetchSignaturesForSettingsTableAsync: fetchSignaturesForSettingsTableAsyncAction,
  markSignaturesAsStale: markSignaturesAsStaleAction,
}));

export const SignaturesTableContainer = connect(mapStateToProps, mapDispatchToProps)(SignaturesTable);
